/**
 * Date utilities for the planner system
 * 
 * CONVENTION:
 * - Dates for plantings and field actions are sent to/from the backend as YYYY-MM-DD strings
 * - The backend interprets these dates in the farm's timezone
 * - Date pickers work in the user's local time, but we convert to/from YYYY-MM-DD for API calls
 */

/**
 * Format a Date object to YYYY-MM-DD string for API requests
 * Uses local date components to preserve the user's intended date
 * 
 * @param date - Date object from date picker
 * @returns Date string in YYYY-MM-DD format, or null if date is null/undefined
 */
export function formatDateForApi(date: Date | null | undefined): string | null {
  if (!date) return null;
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Parse a YYYY-MM-DD string from API to a Date object for date pickers
 * Creates a date at midnight in the local timezone
 * 
 * @param dateStr - Date string in YYYY-MM-DD format from API
 * @returns Date object, or null if string is null/undefined/invalid
 */
export function parseDateFromApi(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;
  
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1; // Month is 0-indexed
  const day = parseInt(match[3], 10);
  
  const date = new Date(year, month, day);
  
  // Validate the date components match (catches invalid dates)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }
  
  return date;
}

/**
 * Format a date string with optional farm timezone display
 * Useful for showing dates in the UI with timezone context
 * 
 * @param dateStr - Date string in YYYY-MM-DD format
 * @param farmTimezone - Optional farm timezone (e.g., 'America/New_York')
 * @returns Formatted date string, or empty string if date is invalid
 */
export function formatDateDisplay(
  dateStr: string | null | undefined,
  farmTimezone?: string | null
): string {
  if (!dateStr) return '';
  
  const date = parseDateFromApi(dateStr);
  if (!date) return '';
  
  // Format as readable date
  const formatted = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  
  // Optionally append timezone info
  if (farmTimezone) {
    return `${formatted} (${farmTimezone})`;
  }
  
  return formatted;
}

/**
 * Get today's date as a YYYY-MM-DD string in the local timezone
 * Useful for default date values
 * 
 * @returns Today's date in YYYY-MM-DD format
 */
export function getTodayLocal(): string {
  const today = new Date();
  const formatted = formatDateForApi(today);
  // formatDateForApi should never return null for a valid Date object
  return formatted!;
}

/**
 * Check if a date string is valid YYYY-MM-DD format
 * 
 * @param dateStr - String to validate
 * @returns true if valid, false otherwise
 */
export function isValidDateString(dateStr: string): boolean {
  if (!dateStr) return false;
  
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateStr)) return false;
  
  const date = parseDateFromApi(dateStr);
  return date !== null;
}
